import React, { useEffect, useState, useCallback } from "react";
import { auth } from "../firebase";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";

import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  listAll,
} from "firebase/storage";

const AudioLibraryPage = () => {
  const storage = getStorage();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [files, setFiles] = useState([]);
  const [filter, setFilter] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const loadFiles = useCallback(async () => {
    try {
      const baseRef = ref(storage, "audio-library/");
      async function walk(dirRef) {
        const res = await listAll(dirRef);
        const here = await Promise.all(
          res.items.map(async (itemRef) => ({
            path: itemRef.fullPath,
            name: itemRef.name,
            url: await getDownloadURL(itemRef),
          }))
        );
        let children = [];
        for (const folder of res.prefixes) {
          const child = await walk(folder);
          children = children.concat(child);
        }
        return [...here, ...children];
      }
      const all = await walk(baseRef);
      setFiles(all.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) {
      setFiles([]);
    }
  }, [storage]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const onUpload = (file) => {
    if (!file) return;
    const user = auth.currentUser;
    const safeName = `${Date.now()}_${file.name.replace(/\s+/g, "_")}`;
    const path = `audio-library/${user ? user.uid : "anonymous"}/${safeName}`;
    const storageRef = ref(storage, path);
    const task = uploadBytesResumable(storageRef, file, {
      contentType: file.type || "audio/aac",
    });
    setUploading(true);
    setProgress(0);
    task.on(
      "state_changed",
      (snap) => {
        const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
        setProgress(pct);
      },
      (error) => {
        setUploading(false);
        setSnackbar({
          open: true,
          message: `Yükleme hatası: ${error.message}`,
          severity: "error",
        });
      },
      async () => {
        setUploading(false);
        // Dosya listesini yüklemeye çalış
        await loadFiles();
        // Ayrıca doğrudan bu dosyanın URL'ini ekle (listAll yetki sorunu olursa görünür olsun)
        try {
          const url = await getDownloadURL(storageRef);
          setFiles((prev) => [
            { path, name: safeName, url },
            ...prev.filter((x) => x.path !== path),
          ]);
        } catch (_) {}
        setSnackbar({
          open: true,
          message: "Yükleme tamamlandı",
          severity: "success",
        });
      }
    );
  };

  const filtered = files.filter((f) =>
    f.name.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <Paper sx={{ p: 3, borderRadius: 5 }}>
      <Typography variant="h4" gutterBottom>
        Ses Kütüphanesi
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Button variant="contained" component="label">
          MP3 Yükle
          <input
            type="file"
            hidden
            accept="audio/*"
            onChange={(e) => onUpload(e.target.files?.[0])}
          />
        </Button>
        {uploading && (
          <Box sx={{ flexGrow: 1 }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}
        <TextField
          placeholder="Ara (dosya adı)"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          size="small"
        />
      </Box>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Dosya</TableCell>
            <TableCell>Önizleme</TableCell>
            <TableCell align="right">İşlem</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map((f) => (
            <TableRow key={f.path}>
              <TableCell>{f.name}</TableCell>
              <TableCell>
                <audio
                  src={f.url}
                  controls
                  preload="none"
                  style={{ width: 240 }}
                />
              </TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(f.url);
                    setSnackbar({
                      open: true,
                      message: "URL kopyalandı",
                      severity: "success",
                    });
                  }}
                >
                  URL'yi Kopyala
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AudioLibraryPage;

// Inline, lightweight picker for embedding in forms
export const AudioPicker = ({ value, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const [list, setList] = React.useState([]);
  const storage = getStorage();

  const load = React.useCallback(async () => {
    try {
      const baseRef = ref(storage, "audio-library/");
      async function walk(dirRef) {
        const res = await listAll(dirRef);
        const here = await Promise.all(
          res.items.map(async (itemRef) => ({
            path: itemRef.fullPath,
            name: itemRef.name,
            url: await getDownloadURL(itemRef),
          }))
        );
        const subs = await Promise.all(res.prefixes.map((p) => walk(p)));
        return [...here, ...subs.flat()];
      }
      const items = await walk(baseRef);
      setList(items.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (_) {
      setList([]);
    }
  }, [storage]);

  React.useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Box
      sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}
    >
      <TextField
        sx={{ minWidth: 280 }}
        label="Ses URL'i"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Seç
      </Button>
      {open && (
        <Paper sx={{ p: 1, maxHeight: 260, overflow: "auto", minWidth: 360 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Ses Kütüphanesi
          </Typography>
          {list.map((f) => (
            <Box
              key={f.path}
              sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}
            >
              <Button size="small" onClick={() => onChange(f.url)}>
                Seç
              </Button>
              <Typography
                variant="body2"
                sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {f.name}
              </Typography>
              <audio src={f.url} controls style={{ height: 28 }} />
            </Box>
          ))}
          <Box sx={{ textAlign: "right", mt: 1 }}>
            <Button size="small" onClick={() => setOpen(false)}>
              Kapat
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};
